import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { $createHeadingNode, HeadingTagType } from "@lexical/rich-text";
import { $isAtNodeEnd, $setBlocksType } from "@lexical/selection";
import { $findMatchingParent } from "@lexical/utils";
import {
  $createParagraphNode,
  $getSelection,
  $isRootOrShadowRoot,
  ElementNode,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  TextNode,
} from "lexical";

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(
    () => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createParagraphNode());
    },
    { onUpdate: () => editor.focus() }
  );
};

export const formatHeading = (
  editor: LexicalEditor,
  blockType: string,
  headingSize: HeadingTagType
) => {
  if (blockType !== headingSize) {
    editor.update(
      () => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      },
      { onUpdate: () => editor.focus() }
    );
  }
};

export const formatBulletList = (editor: LexicalEditor, blockType: string) => {
  if (blockType === "bullet") {
    editor.update(
      () => {
        // Dispatch the command within an update block
        const success = editor.dispatchCommand(
          INSERT_UNORDERED_LIST_COMMAND,
          undefined
        );
        return success;
      },
      { onUpdate: () => editor.focus() }
    );
  } else {
    formatParagraph(editor);
  }
};

export const formatNumberedList = (
  editor: LexicalEditor,
  blockType: string
) => {
  if (blockType === "number") {
    editor.update(
      () => {
        // Dispatch the command within an update block
        const success = editor.dispatchCommand(
          INSERT_ORDERED_LIST_COMMAND,
          undefined
        );
        return success;
      },
      { onUpdate: () => editor.focus() }
    );
  } else {
    formatParagraph(editor);
  }
};

export const getSelectedNode = (
  selection: RangeSelection
): TextNode | ElementNode => {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
};

export const findTopLevelElement = (node: LexicalNode) => {
  let topLevelElement =
    node.getKey() === "root"
      ? node
      : $findMatchingParent(node, (e) => {
          const parent = e.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        });

  if (topLevelElement === null) {
    topLevelElement = node.getTopLevelElementOrThrow();
  }
  return topLevelElement;
};

export const rootTypeToRootName = {
  root: "Root",
  table: "Table",
};

export const blockTypeToBlockName = {
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  paragraph: "Normal",
  bullet: "Bullet List",
  number: "Numbered List",
};

export const INITIAL_TOOLBAR_STATE = {
  blockType: "paragraph" as keyof typeof blockTypeToBlockName,
  canRedo: false,
  canUndo: false,
  isBold: false,
  isItalic: false,
  isStrikethrough: false,
  isUnderline: false,
  rootType: "root" as keyof typeof rootTypeToRootName,
};

export type ToolbarState = typeof INITIAL_TOOLBAR_STATE;
